import { useState } from 'react'

import { getManifest } from '@senses/games'

import { ParamSummary } from './ParamForm.tsx'
import { TrialStrip } from './ProgressChart.tsx'
import { Alert, Empty, Modal, Spinner } from './ui.tsx'
import { api, type TrainingSession, type Trial } from '../lib/api.ts'
import { useAsync } from '../lib/hooks.ts'
import { formatDateTime, formatDuration, formatNumber } from '../lib/format.ts'

/**
 * Výpis odtrénovaných sérií s rozklikávacím detailem.
 *
 * Používá se pod grafem vývoje, kde ukazuje právě ty série, ze kterých
 * je graf sestavený — díky tomu na sebe obojí sedí a nemusí se přepínat
 * mezi dvěma stránkami.
 */
export function SessionTable({
  sessions,
  loading,
  error,
  showProgram = true,
  emptyTitle = 'Zatím žádné odtrénované série',
  emptyHint,
  highlightId = null,
  detailId: controlledDetailId,
  onDetailChange,
  onHover,
}: {
  sessions: TrainingSession[]
  loading?: boolean
  error?: string | null
  /** Sloupec s programem se skrývá tam, kde je stejný pro všechny řádky. */
  showProgram?: boolean
  emptyTitle?: string
  emptyHint?: string
  /** Řádek této série se podbarví — používá graf při najetí na bod. */
  highlightId?: number | null
  /** Když je zadané, otevřený detail řídí rodič; jinak si ho tabulka drží sama. */
  detailId?: number | null
  onDetailChange?: (id: number | null) => void
  onHover?: (id: number | null) => void
}): React.JSX.Element {
  const [ownDetailId, setOwnDetailId] = useState<number | null>(null)

  const detailId = controlledDetailId !== undefined ? controlledDetailId : ownDetailId
  const setDetailId = (id: number | null): void => {
    if (onDetailChange) onDetailChange(id)
    else setOwnDetailId(id)
  }

  if (loading) return <Spinner />
  if (error) return <Alert>{error}</Alert>

  if (sessions.length === 0) {
    return (
      <Empty title={emptyTitle}>{emptyHint && <p>{emptyHint}</p>}</Empty>
    )
  }

  return (
    <>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Kdy</th>
              {showProgram && <th>Program</th>}
              <th className="num">Výsledek</th>
              <th className="num">Úspěšnost</th>
              <th className="num">Pokusů</th>
              <th className="num">Čas</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {sessions.map((session) => (
              <SessionRow
                key={session.id}
                session={session}
                showProgram={showProgram}
                highlighted={session.id === highlightId}
                onDetail={() => setDetailId(session.id)}
                onHover={onHover}
              />
            ))}
          </tbody>
        </table>
      </div>

      {detailId !== null && <SessionDetail id={detailId} onClose={() => setDetailId(null)} />}
    </>
  )
}

function SessionRow({
  session,
  showProgram,
  highlighted,
  onDetail,
  onHover,
}: {
  session: TrainingSession
  showProgram: boolean
  highlighted: boolean
  onDetail: () => void
  onHover?: (id: number | null) => void
}): React.JSX.Element {
  const manifest = getManifest(session.gameId)
  const precision = manifest?.primaryMetric.precision ?? 2

  return (
    <tr
      className={`row--clickable ${highlighted ? 'row--highlight' : ''}`}
      style={{ opacity: session.completed ? 1 : 0.62 }}
      onMouseEnter={() => onHover?.(session.id)}
      onMouseLeave={() => onHover?.(null)}
      onClick={onDetail}
    >
      <td style={{ whiteSpace: 'nowrap' }}>
        {formatDateTime(session.finishedAt)}
        {!session.completed && (
          <span className="badge badge--warning" style={{ marginLeft: '0.5rem' }}>
            nedokončeno
          </span>
        )}
      </td>
      {showProgram && (
        <td>{session.programName ?? <span className="dim">bez programu</span>}</td>
      )}
      <td className="num">
        <strong>{formatNumber(session.score, precision)}</strong>
      </td>
      <td className="num">{Math.round(session.accuracy * 100)} %</td>
      <td className="num">{session.trialCount}</td>
      <td className="num">{formatDuration(session.durationMs)}</td>
      <td>
        {/* Tlačítko zůstává kvůli ovládání klávesnicí a jako vodítko, že
            se řádek dá rozkliknout. Kliknutí se nesmí propagovat na řádek,
            jinak by se detail otevíral dvakrát. */}
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={(event) => {
            event.stopPropagation()
            onDetail()
          }}
        >
          Detail
        </button>
      </td>
    </tr>
  )
}

function SessionDetail({ id, onClose }: { id: number; onClose: () => void }): React.JSX.Element {
  const detail = useAsync(() => api.sessions.get(id), [id])

  return (
    <Modal title="Detail série" onClose={onClose} wide>
      {detail.loading && <Spinner />}
      {detail.error && <Alert>{detail.error}</Alert>}

      {detail.data && (
        <SessionDetailBody session={detail.data.session} trials={detail.data.trials} />
      )}
    </Modal>
  )
}

function SessionDetailBody({
  session,
  trials,
}: {
  session: TrainingSession
  trials: Trial[]
}): React.JSX.Element {
  const manifest = getManifest(session.gameId)
  const metrics = [manifest?.primaryMetric, ...(manifest?.secondaryMetrics ?? [])].filter(
    (m) => m !== undefined,
  )

  return (
    <>
      <p className="muted" style={{ marginBottom: '1.25rem' }}>
        {manifest?.name ?? session.gameId}
        {session.programName ? ` · ${session.programName}` : ''} · {formatDateTime(session.finishedAt)}
      </p>

      <div className="stats" style={{ marginBottom: '1.5rem' }}>
        {metrics.map((metric) => {
          const value = session.metrics[metric.key]
          if (value === undefined) return null
          return (
            <div className="stat" key={metric.key}>
              <div className="stat__label">{metric.label}</div>
              <div className="stat__value">
                {formatNumber(value, metric.precision)}
                {metric.unit && <span className="stat__hint"> {metric.unit}</span>}
              </div>
            </div>
          )
        })}
      </div>

      {/* Parametry se ke každé sérii ukládají zvlášť, takže i po pozdější
          úpravě programu je vidět, za jakých podmínek výsledek vznikl. */}
      {manifest && (
        <div style={{ marginBottom: '1.5rem' }}>
          <div className="stat__label" style={{ marginBottom: '0.5rem' }}>
            Nastavení tréninku
          </div>
          <ParamSummary manifest={manifest} params={session.params} />
        </div>
      )}

      {trials.length > 0 && (
        <>
          <div className="stat__label" style={{ marginBottom: '0.5rem' }}>
            Průběh obtížnosti
          </div>
          <TrialStrip trials={trials} />

          <div className="table-wrap" style={{ marginTop: '1.25rem' }}>
            <table>
              <thead>
                <tr>
                  <th className="num">#</th>
                  <th className="num">Obtížnost</th>
                  <th>Výsledek</th>
                  <th className="num">Trefeno</th>
                  <th className="num">Odpověď</th>
                </tr>
              </thead>
              <tbody>
                {trials.map((trial) => (
                  <tr key={trial.index}>
                    <td className="num dim">{trial.index + 1}</td>
                    <td className="num">{formatNumber(trial.difficulty, 3)}</td>
                    <td>
                      <span className={`badge ${trial.correct ? 'badge--success' : 'badge--danger'}`}>
                        {trial.correct ? 'správně' : 'chyba'}
                      </span>
                    </td>
                    <td className="num">
                      {trial.hits} / {trial.targets}
                    </td>
                    <td className="num">{(trial.responseMs / 1000).toFixed(1)} s</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  )
}
